import { useEffect } from 'react';
import { Button, Stack } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconShoppingBag } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { useOffer } from '@/features/access/PlanOffer';
import { CHECKOUT_PRODUCTS_KEY, fetchCheckoutProducts } from '@/features/admin/payments-api';
import { takeReturnTo } from '@/lib/returnTo';
import { CheckoutProducts } from './CheckoutProducts';

/**
 * Платёжная ссылка товара: `/buy/<код>` (из «Тарифы и оплата»). Её ставят на
 * сайт, в Telegram, в письмо. Без входа человек сначала входит или
 * регистрируется — после входа его возвращает сюда, — затем видит карточку
 * товара с ценой и кнопкой оплаты. Цену и права решает сервер, как везде.
 */
export function BuyPage() {
  const { code = '' } = useParams<{ code: string }>();
  const offer = useOffer();
  const catalog = useQuery({ queryKey: CHECKOUT_PRODUCTS_KEY, queryFn: fetchCheckoutProducts, staleTime: 0 });
  // Дошли до оплаты — запомненный при входе путь больше не нужен.
  useEffect(() => void takeReturnTo(), []);

  if (offer.isLoading || catalog.isLoading) return <LoadingState />;
  if (catalog.isError) return <ErrorState onRetry={() => void catalog.refetch()} />;

  const product = catalog.data?.items.find((item) => item.code === code);
  if (!offer.data?.direct || !product) {
    return (
      <>
        <PageHeader eyebrow="Оплата" title="Товар не найден" />
        <EmptyState
          icon={<IconShoppingBag size={28} stroke={1.5} />}
          title="Этот товар сейчас не продаётся"
          description="Возможно, ссылка устарела. Посмотрите, что доступно сейчас."
          action={
            <Button component={Link} to="/tariffs" variant="light" size="xs">
              К тарифам
            </Button>
          }
        />
      </>
    );
  }

  return (
    <Stack gap="md" maw={520}>
      <PageHeader eyebrow="Оплата" title="Покупка" description="Оплата через Robokassa. Доступ откроется сразу после оплаты." />
      <CheckoutProducts filter={(item) => item.code === code} compact />
    </Stack>
  );
}
